import { HTMLAttributes } from 'react';

/**
 * The design's status pill (OO1): the ONE round element in the system. Semibold 11px, the
 * design system's semantic colors - green for cleared / shared, amber for pending / waived,
 * blue for information (stage), KW red for overdue or "for sale", black for "sold" / "sale".
 */
type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' | 'dark' | 'outline';
type BadgeSize = 'sm' | 'md';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-700',
  success: 'bg-success-wash text-success',
  warning: 'bg-warning-wash text-warning',
  error: 'bg-primary-100 text-primary-500',
  info: 'bg-info-wash text-info',
  primary: 'bg-primary-500 text-white',
  dark: 'bg-gray-900 text-white',
  outline: 'bg-white text-gray-800 border border-gray-200',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2.5 py-1 text-[11px]',
  md: 'px-3 py-1.5 text-xs',
};

export const Badge = ({
  variant = 'default',
  size = 'sm',
  className = '',
  children,
  ...props
}: BadgeProps) => {
  return (
    <span
      className={`
        inline-flex items-center font-semibold leading-none rounded-full
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `}
      {...props}
    >
      {children}
    </span>
  );
};

/**
 * The design's uppercase category tag (3px corners, hairline border, letter-spaced) - for
 * document categories, property types, listing states. Pills are for STATUS; tags are for
 * KIND.
 */
export const Tag = ({ className = '', children, ...props }: HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={`inline-flex items-center rounded-sm border border-gray-200 bg-white px-[7px] py-1 text-[9.5px] font-bold uppercase leading-none tracking-[.1em] text-gray-800 ${className}`}
    {...props}
  >
    {children}
  </span>
);

// Property type badge with automatic coloring
export const PropertyTypeBadge = ({ type }: { type: string }) => {
  const typeColors: Record<string, BadgeVariant> = {
    warehouse: 'info',
    distribution_center: 'primary',
    manufacturing: 'warning',
    flex_space: 'success',
    cold_storage: 'info',
    industrial: 'default',
    office: 'success',
    retail: 'warning',
    land: 'default',
  };

  const displayName = type?.replace(/_/g, ' ') || 'Unknown';
  const variant = typeColors[type] || 'default';

  return (
    <Badge variant={variant} size="sm">
      {displayName.charAt(0).toUpperCase() + displayName.slice(1)}
    </Badge>
  );
};
